<script setup lang="ts">
import LeTooltip from "@dsk/components/LeTooltip.vue";
import { words } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, h, type VNode } from "vue";

import { useSearchFromResult } from "@/composables";
import { MatomoHelper } from "@/helpers";
import { useSearchStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const props = defineProps<{
  translation: string;
  usageLanguage: string;
}>();

const { selectedDictionary } = storeToRefs(useSearchStore());
const { searchOppositeDirection } = useSearchFromResult();

const tooltipText = computed<string>(() =>
  props.usageLanguage === "da" ? "Klik for at kopiere" : "Zum Kopieren klicken"
);
const tooltipClickedText = computed<string>(() => (props.usageLanguage === "da" ? "Kopieret!" : "Kopiert!"));

const trackClickToCopy = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickedToCopyTranslation,
    "Clicked a translation to copy in Dictsys Article"
  );
};

const trackDoubleClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.DoubleClickTranslation,
    "Double clicked a translation in Dictsys Article"
  );
};

const click = async () => {
  await navigator.clipboard.writeText(props.translation);
  trackClickToCopy();
};

const SearchableText = computed<VNode>(() => {
  const elements: (VNode | string)[] = [];
  let translation = props.translation;

  words(props.translation).forEach((word) => {
    // Some content is not a word, but spaces, commas and so on
    const i = translation.indexOf(word);

    if (i > 0) {
      elements.push(translation.slice(0, i));
    }
    elements.push(
      h(
        "span",
        {
          ondblclick: () => {
            trackDoubleClick();
            searchOppositeDirection(word, selectedDictionary.value);
          }
        },
        word
      )
    );

    translation = translation.slice(i + word.length);
  });

  // Add any leftovers, after the last word, such as a question mark
  if (translation.length > 0) {
    elements.push(translation);
  }

  return h("span", elements);
});
</script>

<template>
  <LeTooltip
    :text="tooltipText"
    :text-on-target-click="tooltipClickedText"
    :delay="1000"
    :margin-bottom="10"
    class="le-article-translation__tooltip"
    @click="click()"
  >
    <SearchableText class="le-article-translation" />
  </LeTooltip>
</template>

<style lang="less" scoped>
.le-article-translation {
  margin-left: -2px;
  padding: 1px 2px;

  &,
  &__tooltip {
    pointer-events: none;

    @media (hover: hover) {
      pointer-events: auto;

      &:hover {
        background-color: #c9e9ff;
        color: #325768;
        cursor: pointer;
      }
    }
  }
}
</style>
